/* eslint-disable @typescript-eslint/no-explicit-any */
import Highcharts from "highcharts";
import SelectionMarker from "../lib/selectionMarker";

declare module "highcharts" {
  interface Chart {
    hasCartesianSeries: boolean;
    pan: (e: PointerEventObject, b: ChartPanningOptions | undefined) => void;
  }

  interface Pointer {
    chart: Chart;
    zoomX: boolean;
    zoomY: boolean;
    zoomHor: boolean;
    zoomVert: boolean;
    mouseDownX?: number;
    mouseDownY?: number;
    selectionMarker?: SelectionMarker;
    hasCartesianSeries: boolean;
    hasDragged: boolean;
  }
}

let isBetterZoomEnabled = true;

export const MIN_DELTA = 30;

export default function betterZoom(H: typeof Highcharts) {
  // fix Pointer.unbindDocumentMouseUp handling for Highcharts 9.1.0
  H.wrap(
    H.Pointer.prototype,
    "destroy",
    function (this: Highcharts.Pointer, proceed: any, ...args: unknown[]) {
      // https://github.com/highcharts/highcharts/blob/6ffbe8ef112b50245aed69de6ee812c5823621fa/ts/Core/Pointer.ts#L244
      (H as any).chartCount = 1; // do not remove mouseUp listener
      proceed.apply(this, args);
    },
  );

  H.wrap(
    H.Pointer.prototype,
    "drag",
    function (
      this: Highcharts.Pointer,
      proceed: {
        apply: (arg0: Highcharts.Pointer, arg1: Highcharts.PointerEventObject[]) => void;
      },
      e: Highcharts.PointerEventObject,
    ) {
      if (!isBetterZoomEnabled) {
        proceed.apply(this, [e]); // Use the original drag behavior
        return;
      }

      // https://github.com/highcharts/highcharts/blob/5e3927f5d3922c72c40d64a028dbc8a2e81bb34e/js/Core/Pointer.js#L219
      const chart = this.chart,
        chartOptions = chart.options.chart,
        plotLeft = chart.plotLeft,
        plotTop = chart.plotTop,
        plotWidth = chart.plotWidth,
        plotHeight = chart.plotHeight,
        mouseDownX = this.mouseDownX ?? 0,
        mouseDownY = this.mouseDownY ?? 0,
        panningEnabled = Highcharts.isObject(chartOptions?.panning)
          ? chartOptions?.panning?.enabled
          : chartOptions?.panning;

      const key = (chartOptions?.panKey + "Key") as "metaKey" | "ctrlKey" | "altKey";
      const panKey = chartOptions?.panKey && e[key];

      if (panKey && panningEnabled) {
        return chart.pan(e, chartOptions?.panning);
      }

      let selectionMarker = this.selectionMarker;
      let chartX = e.chartX,
        chartY = e.chartY;

      // If the device supports both touch and mouse (like IE11), and we are
      // touch-dragging inside the plot area, don't handle the mouse event.
      // #4339.
      const marker = selectionMarker as (SVGElement & { touch: boolean }) | undefined;
      if (marker?.touch) {
        return;
      }
      // If the mouse is outside the plot area, adjust to cooordinates
      // inside to prevent the selection marker from going outside
      if (chartX < plotLeft) {
        chartX = plotLeft;
      } else if (chartX > plotLeft + plotWidth) {
        chartX = plotLeft + plotWidth;
      }
      if (chartY < plotTop) {
        chartY = plotTop;
      } else if (chartY > plotTop + plotHeight) {
        chartY = plotTop + plotHeight;
      }

      const zoomXActive = this.zoomX && Math.abs(chartX - mouseDownX) > MIN_DELTA;
      const zoomYActive = this.zoomY && Math.abs(chartY - mouseDownY) > MIN_DELTA;

      const hasDragged = zoomXActive || zoomYActive;
      if (hasDragged) {
        this.hasDragged = true;
        const clickedInside = chart.isInsidePlot(mouseDownX - plotLeft, mouseDownY - plotTop);
        // make a selection
        if (chart.hasCartesianSeries && (this.zoomX || this.zoomY) && clickedInside && !panKey) {
          if (!selectionMarker) {
            this.selectionMarker = selectionMarker = new SelectionMarker(chart, MIN_DELTA);
          }
        }

        if (selectionMarker) {
          selectionMarker.render(
            { x: mouseDownX, y: mouseDownY },
            { x: chartX, y: chartY },
            zoomXActive,
            zoomYActive,
          );
        }

        // panning
        // if (clickedInside && !selectionMarker && panningEnabled) {
        //   chart.pan(e, chartOptions?.panning);
        // }
      } else if (this.hasDragged) {
        this.hasDragged = false;
        if (selectionMarker) {
          selectionMarker.destroy();
          this.selectionMarker = undefined;
        }
      }
    },
  );
}

export function toggleBetterZoom(enable: boolean) {
  isBetterZoomEnabled = enable; // Set the flag to enable or disable this plugin
}
